import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CookiePreferencesModal from '../cookie-consent/CookiePreferencesModal';
import { useLanguage } from '../../hooks/useLanguage';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import api from '../../services/api';

interface FooterProps {
  isHomePage?: boolean;
}

const Footer: React.FC<FooterProps> = ({ isHomePage = false }) => {
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  const [hasActiveProducts, setHasActiveProducts] = useState(false);
  const { t } = useLanguage();

  // Check if there are active products
  useEffect(() => {
    const checkActiveProducts = async () => {
      try {
        const response = await api.get('/products');
        const products = response.data || [];
        setHasActiveProducts(products.length > 0);
      } catch (error) {
        console.error('Error checking products:', error);
        setHasActiveProducts(false);
      }
    };

    checkActiveProducts();
  }, []);

  return (
    <>
      {/* Mini Footer for Creative Commons Attribution - Only on Home Page */}
      {isHomePage && (
        <div className="bg-gray-900 text-gray-300 py-2">
          <div className="w-full px-4">
            <div className="text-center text-xs">
              <span>{t('landing.footer.creativeCommons')}</span>
              <a 
                href="/commons.wikimedia.info" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                {t('landing.footer.fullCredits')}
              </a>
            </div>
          </div>
        </div>
      )}
      
      <footer id="colophon" className="site-footer bg-gray-800 text-white">
        <div className="footer-content py-12">
          <div className="w-full px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Company Info */}
              <div className="company-info">
                <div className="flex items-center mb-4">
                  <img 
                    width="40" 
                    height="40" 
                    src="/logo.ico" 
                    className="mr-3 w-20 h-20 max-w-20 max-h-20" 
                    alt="La Dolce Vita" 
                  />
                  <h3 className="text-xl font-bold font-serif italic">La Dolce Vita</h3>
                </div>
                <p className="text-gray-300 mb-4">
                  {t('landing.footer.companyDescription')}
                </p>
                <div className="text-sm text-gray-400 space-y-1">
                  <p>📍 Via Roma 123, 65000 Pescara, Italia</p>
                  <p>📞 +39 085 123 4567</p>
                  <p>
                    📧 <a href="mailto:info@ladolcevitareis.nl" className="underline hover:text-white">info@ladolcevitareis.nl</a>
                  </p>
                  <p>🏢 P.IVA: 12345678901</p>
                </div>
              </div>

              {/* Quick Links */}
              <div className="quick-links">
                <h4 className="text-lg font-semibold mb-4">{t('landing.footer.quickLinks')}</h4>
                <ul className="space-y-2">
                  <li>
                    <Link to="/" className="text-gray-300 hover:text-white transition-colors">
                      {t('common.home')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/#trips" className="text-gray-300 hover:text-white transition-colors">
                      {t('landing.trips.title')}
                    </Link>
                  </li>
                  <li>
                    <a href="/#destinations" className="text-gray-300 hover:text-white transition-colors">
                      {t('landing.destinations.title')}
                    </a>
                  </li>
                  <li>
                    <a href="/#activities" className="text-gray-300 hover:text-white transition-colors">
                      {t('landing.activities.title')}
                    </a>
                  </li>
                  <li>
                    <a href="/#curiosities" className="text-gray-300 hover:text-white transition-colors">
                      {t('landing.curiosities.title')}
                    </a>
                  </li>
                  {hasActiveProducts && (
                    <li>
                      <Link to="/products" className="text-gray-300 hover:text-white transition-colors">
                        {t('nav.products')}
                      </Link>
                    </li>
                  )}
                  {hasActiveProducts && (
                    <li>
                      <Link to="/shipping-costs" className="text-gray-300 hover:text-white transition-colors">
                        Shipping Costs
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link to="/about-us" className="text-gray-300 hover:text-white transition-colors">
                      {t('landing.footer.about')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-gray-300 hover:text-white transition-colors">
                      {t('landing.footer.contact')}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Social Media */}
              <div className="social-media">
                <h4 className="text-lg font-semibold mb-4">{t('landing.footer.followUs')}</h4>
                <p className="text-gray-300 mb-4">
                  {t('landing.footer.followUsDescription')}
                </p>
                <div className="flex space-x-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61580521581922" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-blue-400 transition-colors"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M22 12.06C22 6.48 17.52 2 11.94 2 6.48 2 2 6.48 2 12.06 2 17.08 5.66 21.21 10.44 22v-7.02H7.9v-2.92h2.54V9.41c0-2.5 1.49-3.88 3.78-3.88 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.89h2.77l-.44 2.92h-2.33V22C18.34 21.21 22 17.08 22 12.06z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.instagram.com/ladolcevitareis" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-pink-400 transition-colors"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3.5A5.5 5.5 0 1112 18.5 5.5 5.5 0 0112 7.5zm0 2A3.5 3.5 0 1015.5 13 3.5 3.5 0 0012 9.5zM18 6.25a1.25 1.25 0 11-1.25 1.25A1.25 1.25 0 0118 6.25z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="border-t border-gray-700 mt-8 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="text-sm text-gray-400 mb-4 md:mb-0">
                  <span>{t('landing.footer.copyright')}</span>
                </div>
                <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                  <div className="flex space-x-6 text-sm">
                    <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                      {t('landing.footer.privacy')}
                    </Link>
                    <Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors">
                      {t('landing.footer.terms')}
                    </Link>
                    <Link to="/privacy-policy#cookies" className="text-gray-400 hover:text-white transition-colors">
                      {t('landing.footer.cookiePolicy')}
                    </Link>
                    <button 
                      onClick={() => setIsCookieModalOpen(true)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {t('landing.footer.manageCookies')}
                    </button>
                  </div>
                  <div className="flex items-center">
                    <LanguageSelector openUpwards={true} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <CookiePreferencesModal 
        isOpen={isCookieModalOpen} 
        onClose={() => setIsCookieModalOpen(false)} 
      />
    </>
  );
}

export default Footer; 